import { Component, Input, OnInit, Output, EventEmitter, OnDestroy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { ApBaseComponent, ApNotificationService } from 'webcommon/core-ui';
import { FcItemModalContextWithPrimaryKey, FcItemModalMode, FcItemModalService } from 'webcommon/find-control';
import { FindControlIdService } from 'webcommon/legacy-common';
import { PatientNavService, PatientTabContext, ViewOptionDto, ViewOptionResponse } from 'webcommon/shared';
import { FindControlRepository, UIFilterRepository } from 'webcommon/webapi';
import { ViewOptionModalService } from '../view-option-modal';

@Component({
  selector: 'ap-view-option-form',
  templateUrl: './view-option-form.component.html',
  styleUrls: ['./view-option-form.component.less'],
})
export class ViewOptionFormComponent extends ApBaseComponent implements OnInit , OnDestroy {
  private guid = '00000000-0000-0000-0000-000000000000';
  public baseId = 'vof-' + Math.random().toString(36);
  @Input() actionObs: Observable<string>;
  @Output() saved = new EventEmitter<string | null>();
  @Output() isClickable = new EventEmitter<boolean>(false);
  @Input() fcModalItemContext!: FcItemModalContextWithPrimaryKey;
  @Input() formProcessType: 'new' | 'view' | 'edit' = 'view';
  @Input() customData: ViewOptionDto;
  @Input() mode: 'findControl' | 'link' = 'findControl';

  public actionSubscription: Subscription;
  public billingProviderIds: string[] | null = [];
  public renderingProviderIds: string[] | null = [];
  public messageOptionsIds: string[] | null = [];
  public viewOptionId: string | null;
  public medicationFilterId: string | null;
  public problemFilterId: string | null;

  public rpnOptionsUid = new FormControl<string>('');
  public viewOptionUid = new FormControl<string[] | null>(null);
  public useTerseMode = new FormControl<boolean>(false);
  public vitalsAsMetrics = new FormControl<boolean>(false);
  public rpnInitialCategory = new FormControl<string>('Visits');
  public showPrivateVisits = new FormControl<boolean>(false);
  public showStruckOutVisits = new FormControl<boolean>(false);
  public showLabVisits = new FormControl<boolean>(false);
  public showRefillVisits = new FormControl<boolean>(false);
  public showDisplayPdfVisits = new FormControl<boolean>(false);
  public showAddendumNotesInTerseMode = new FormControl<boolean>(false);
  public includeArchiveNotesInAttachmentsCategory = new FormControl<boolean>(true);
  public includeConfidentialComments = new FormControl<boolean>(false);
  public showAllMessages = new FormControl<boolean>(false);
  public billingProviderType = new FormControl<string>('List');
  public renderingProviderType = new FormControl<string>('List');
  public messageType = new FormControl<string>('Show All Types');
  public billingProvidersFilterList = new FormControl<string[] | null>(null);
  public renderingProvidersFilterList = new FormControl<string[] | null>(null);
  public noteFormatTemplateUid = new FormControl<string | null>(this.guid);
  public messageTypesFilterList = new FormControl<string[] | null>(null);
  public messageSubtypesFilterList = new FormControl<string[] | null>(null);
  public externalId = new FormControl<string | null>(null);
  public name = new FormControl<string>('', [Validators.required]);
  public inActive = new FormControl<boolean>(false);
  public scopeOfOptions = new FormControl<string | null>('Anyone');

  public filterForm = new FormGroup({
    rpnOptionsUid: this.rpnOptionsUid,
    viewOptionUid: this.viewOptionUid,
    useTerseMode: this.useTerseMode,
    vitalsAsMetrics: this.vitalsAsMetrics,
    rpnInitialCategory: this.rpnInitialCategory,
    showPrivateVisits: this.showPrivateVisits,
    showStruckOutVisits: this.showStruckOutVisits,
    showLabVisits: this.showLabVisits,
    showRefillVisits: this.showRefillVisits,
    showDisplayPdfVisits: this.showDisplayPdfVisits,
    showAddendumNotesInTerseMode: this.showAddendumNotesInTerseMode,
    includeArchiveNotesInAttachmentsCategory: this.includeArchiveNotesInAttachmentsCategory,
    includeConfidentialComments: this.includeConfidentialComments,
    showAllMessages: this.showAllMessages,
    billingProviderType: this.billingProviderType,
    renderingProviderType: this.renderingProviderType,
    messageType: this.messageType,
    billingProvidersFilterList: this.billingProvidersFilterList,
    renderingProvidersFilterList: this.renderingProvidersFilterList,
    noteFormatTemplateUid: this.noteFormatTemplateUid,
    messageTypesFilterList: this.messageTypesFilterList,
    messageSubtypesFilterList: this.messageSubtypesFilterList,
    externalId: this.externalId,
    name: this.name,
    inActive: this.inActive,
    scopeOfOptions: this.scopeOfOptions
  });

  public scopeOptions = [
    { label: 'Available to Anyone', value: 'Anyone' },
    { label: 'Just for Me', value: 'Me' }
  ];

  public initialRpnCategories = [
    { label: 'All Interaction', value: 'All' },
    { label: 'Visit Note', value: 'Visits' },
    { label: 'Messages', value: 'Messages' },
    { label: 'Attachment', value: 'Attachments' },
    { label: 'Results', value: 'Results' }
  ];

  public categoryOptionsList = [
    { label: 'Include Private Visits', controlName: 'showPrivateVisits' },
    { label: 'Include Struck Out Visits', controlName: 'showStruckOutVisits' },
    { label: 'Include Laboratory Results Visits', controlName: 'showLabVisits' },
    { label: 'Include Refill Requests Visits', controlName: 'showRefillVisits' },
    { label: 'Visit Category Display PDF', controlName: 'showDisplayPdfVisits' },
    { label: 'Include Archive Notes in Attachments Category', controlName: 'includeArchiveNotesInAttachmentsCategory' }
  ];

  public billingProviderOptions = [
    { label: 'Me', value: 'Me' },
    { label: 'Billing Providers', value: 'List' }
  ];

  public renderingProviderOptions = [
    { label: 'Me', value: 'Me' },
    { label: 'Rendering Providers', value: 'List' }
  ];

  public messageProviderOptions = [
    { label: 'Show All Types', value: 'Show All Types' },
    { label: 'Show Only', value: 'Show Only' }
  ];

  public isRenderingProviderDisabled = false;

  public isBillingProviderDisabled = false;

  public isMessageOptionDisabled = false;

  public fcItemModalModeEnum = FcItemModalMode;

  constructor(
    public findControlIdService: FindControlIdService,
    private uiFilterRepository: UIFilterRepository,
    private apNotificationService: ApNotificationService,
    private patientNavService: PatientNavService,
    private fcItemModalService: FcItemModalService,
    private viewOptionModalService: ViewOptionModalService,
    private findControlRepo: FindControlRepository
  ) {
    super();
  }
  ngOnInit(): void {
    this.formValuesChangeListeners();
    this.saveFileActionListener();
    this.loadFormProcessType(this.formProcessType);
    this.listenUpdateRequestFromFindControl();
  }

  override ngOnDestroy(): void {
    if (this.actionSubscription) {
      this.actionSubscription.unsubscribe();
    }
  }

  listenUpdateRequestFromFindControl() {
    this.fcItemModalService.viewOptionUpdateEvent$
      .pipe(
        takeUntil(this.onDestroy$),
      )
      .subscribe((viewOptionId) => {
        if (viewOptionId) this.loadSelectedViewOption(viewOptionId);
      });
  }


  loadFormProcessType(action: 'view' | 'edit' | 'new'): void {
    if (action === 'view') {
      if (this.customData?.RPNOptionsUid) this.loadSelectedViewOption(this.customData?.RPNOptionsUid);
    } else if (action === 'edit') {
      const selectedRpnOptionId =
        (this.mode === 'link') ?
          this.customData.RPNOptionsUid :
          this.fcModalItemContext.selectedFindItemPk;
      this.loadSelectedViewOption(selectedRpnOptionId);
    }
  }

  saveFileActionListener(): void {
    this.actionSubscription = this.actionObs.subscribe((actionType: 'view' | 'edit' | 'new' | 'saveApply') => {
      switch (actionType) {
        case 'new':
          this.createFilterOption();
          break;
        case 'edit':
          this.updateFilterOption();
          break;
        case 'view':
          if (this.viewOptionId) this.updateDefaultUserOptionFilter(this.viewOptionId);
          break;
        case 'saveApply':
          this.saveApplyFilter(true);
          break;
        default:
          break;
      }
    });
  }

  // save and apply the filter
  saveApplyFilter(isSaveAndApply: boolean): void {
    this.updateFilterOption(isSaveAndApply);
    if (this.rpnOptionsUid.value) this.updateDefaultUserOptionFilter(this.rpnOptionsUid.value);
  }

  checkMessagesConfig(): void {
    if (this.messageType.value === 'Show Only' &&
      this.messageOptionsIds && this.messageOptionsIds.length >= 1
    ) {
      this.showAllMessages.setValue(false);
    } else {
      this.showAllMessages.setValue(true);
      this.messageSubtypesFilterList.setValue(null);
    }
  }

  updateFilterOption(isSaveAndApply = false) {
    this.checkMessagesConfig();
    const filterOption = this.transformKeys(this.filterForm.value) as ViewOptionDto;
    this.updateFilterViewOption(filterOption, isSaveAndApply);
  }

  createFilterOption() {
    this.checkMessagesConfig();
    const filterOption = this.transformKeys(this.filterForm.value) as ViewOptionDto;
    this.createFilterViewOption(filterOption);
  }

  public onChangeHandler(optionPkId: string, formControlName: string) {
    this.filterForm.enable();
    this.filterForm.patchValue({ [formControlName]: optionPkId });
    if (formControlName === 'rpnOptionsUid' && optionPkId) {
      this.loadSelectedViewOption(optionPkId);
    }
    this.filterForm.disable();
  }

  private listenToChanges = (control: AbstractControl, callback: (value: any) => void) => {
    control.valueChanges.pipe(this.takeUntilDestroy()).subscribe(callback);
  };

  private formValuesChangeListeners(): void {
    if (!this.name.valid) this.isClickable.emit(true);
    this.isMessageOptionDisabled = this.messageType.valid && this.messageType.value === 'Show All Types';
    this.listenToChanges(this.name, (value: string) => {
      this.isClickable.emit(!value?.trim());
    });
    this.listenToChanges(this.billingProviderType, value => {
      this.isBillingProviderDisabled = value === 'Me';
    });
    this.listenToChanges(this.renderingProviderType, value => {
      this.isRenderingProviderDisabled = value === 'Me';
    });
    this.listenToChanges(this.messageType, value => {
      this.isMessageOptionDisabled = value === 'Show All Types';
    });
    this.listenToChanges(this.externalId, value => {
      if (value?.length > 25) {
        this.externalId.setValue(value.substring(0, 25), { emitEvent: false });
      }
    });
  }



  updateDefaultUserOptionFilter(viewOptionId: string): void {
    this.loading = true;
    this.uiFilterRepository.updateUserOptionsFilter(viewOptionId)
      .pipe(
        finalize(() => this.onLoadComplete()),
        takeUntil(this.onDestroy$)
      ).subscribe(
        (options: ViewOptionResponse) => {
          if (this.rpnInitialCategory?.value) {
            const categoryId = this.patientNavService.getInitialCategoryId(this.rpnInitialCategory.value);
            this.patientNavService.patientViewOptionSubject.next({
              updatedContext: {
                context: PatientTabContext.ReviewPastNotes,
                patientId: this.viewOptionModalService.getPatientId,
                isGlassBroken: false
              },
              patientMenuId: categoryId
            });
        }
          this.saved.emit(options.RPNOptionsUid);
          this.apNotificationService.success('Filter updated as a default successfully');
        },
        () => this.onLoadError(`Error while updating the default option to user`)
      );
  }


  updateMruMfuRpnList(selectedPkId: string) {
      this.findControlRepo.updateMfuMruList('ListRPNOptions' , [selectedPkId])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        error: () => {
          this.apNotificationService.error('Failed to update the most frequently used list rpn options');
        }
      });
  }

  onUserSettingChanged(viewOption: ViewOptionDto | null) {
    if (!viewOption) this.isClickable.emit(true);
    else this.isClickable.emit(false);
  }


  loadSelectedViewOption(optionId: string): void {
    this.loading = true;
    this.uiFilterRepository.getSelectedOptionsFilterConfig(optionId)
      .pipe(
        finalize(() => this.onLoadComplete()),
        takeUntil(this.onDestroy$)
      ).subscribe(
        (data: ViewOptionDto) => this.populateDataToFilterForm(data),
        () => this.onLoadError('Error while load the selected view option filter')
      );
  }


  updateFilterViewOption(filterOptionData: ViewOptionDto, isSaveAndApply = false) {
    this.loading = true;
    this.uiFilterRepository.updateSelectedOptionsFilterConfig(filterOptionData)
      .pipe(
        finalize(() => this.onLoadComplete()),
        takeUntil(this.onDestroy$)
      ).subscribe(
        {
          next: ((options: ViewOptionResponse) => {
            if (options && !isSaveAndApply) {
              this.saved.emit(options.RPNOptionsUid);
              this.apNotificationService.success('Filter was updated successfully!');
            }
          }),
          error: (() => this.apNotificationService.error('Failed to Update the Filter Option'))
        }
      );
  }

  createFilterViewOption(filterOptionData: ViewOptionDto) {
    this.loading = true;
    this.uiFilterRepository.addSelectedOptionsFilterConfig(filterOptionData)
      .pipe(
        finalize(() => this.onLoadComplete()),
        takeUntil(this.onDestroy$)
      ).subscribe(
        {
          next: ((options: ViewOptionResponse) => {
            this.updateMruMfuRpnList(options.RPNOptionsUid);
            this.updateDefaultUserOptionFilter(options.RPNOptionsUid);
          }),
          error: (() => this.apNotificationService.error('Failed to create Filter option'))
        }
      );
  }

  private onLoadComplete() {
    this.loading = false;
  }

  private onLoadError(context: string): void {
    this.apNotificationService.error(context);
  }

  populateDataToFilterForm(data: ViewOptionDto) {
    this.filterForm.patchValue({
      rpnOptionsUid: data.RPNOptionsUid,
      name: data.Name,
      externalId: data.ExternalID,
      inActive: data.Inactive,
      scopeOfOptions: data.ScopeOfOptions,
      rpnInitialCategory: data.RPNInitialCategory,
      showPrivateVisits: data.ShowPrivateVisits,
      showStruckOutVisits: data.ShowStruckOutVisits,
      showLabVisits: data.ShowLabVisits,
      showRefillVisits: data.ShowRefillVisits,
      showDisplayPdfVisits: data.ShowDisplayPdfVisits,
      includeArchiveNotesInAttachmentsCategory: data.IncludeArchiveNotesInAttachmentsCategory,
      billingProviderType: data.BillingProviderType === 'All' ? 'List' : data.BillingProviderType,
      renderingProviderType: data.RenderingProviderType === 'All' ? 'List' : data.RenderingProviderType,
      messageTypesFilterList: data.MessageTypesFilterList,
      messageType: data.ShowAllMessages ? 'Show All Types' : 'Show Only',
      billingProvidersFilterList: data.BillingProvidersFilterList,
      renderingProvidersFilterList: data.RenderingProvidersFilterList,
      showAllMessages: data.ShowAllMessages
    });
    this.mapFindControlPkIds();
  }

  mapFindControlPkIds(): void {
    const { renderingProvidersFilterList = [],
      billingProvidersFilterList = [],
      messageTypesFilterList = [],
      rpnOptionsUid = '',
    } = this.filterForm.value;

    this.renderingProviderIds = renderingProvidersFilterList ?? [];

    this.messageOptionsIds = messageTypesFilterList ?? [];

    this.viewOptionId = rpnOptionsUid;

    this.billingProviderIds = billingProvidersFilterList ?? [];

    if (this.formProcessType === 'view') this.filterForm.disable();

  }

  private capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

  private transformKeys = (obj: Record<string, any>): Record<string, any> => {
    const transformed: Record<string, any> = {};
    for (const [key, value] of Object.entries(obj)) {
      transformed[this.capitalizeFirstLetter(key)] = value;
    }
    return transformed;
  };



}
